import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, CheckCircle2, Clock, Sparkles, TrendingUp } from "lucide-react";
import { flags } from "@/config/featureFlags";
import useMotionPreferences from "@/hooks/useMotionPreferences";

/**
 * MarketMap3D — Best of Best Territory Map.
 *
 * Purpose: show category × market claim status at a glance so consumers
 * discover exclusive spots and account executives see open territory.
 *
 * Data model (all optional — component degrades to a beautiful placeholder
 * if `markets` is empty):
 *
 *   markets: [
 *     { city, state, category, status: "claimed" | "available" | "pending" | "sponsored",
 *       business_name?, business_slug? }
 *   ]
 *
 * If no `markets` are passed but `categories` are, we auto-generate a preview
 * grid using the categories × top cities so the section is never empty.
 */
const STATUS_META = {
  claimed: {
    label: "Best of Best Claimed",
    color: "#F59E0B",
    icon: Sparkles,
    intensity: "high",
  },
  sponsored: {
    label: "Sponsored Listing Active",
    color: "#EC4899",
    icon: TrendingUp,
    intensity: "high",
  },
  pending: {
    label: "Pending Approval",
    color: "#00E5FF",
    icon: Clock,
    intensity: "medium",
  },
  available: {
    label: "Available Category",
    color: "#10B981",
    icon: CheckCircle2,
    intensity: "medium",
  },
};

const DEFAULT_CITIES = [
  { city: "Aurora", state: "IL" },
  { city: "Chicago", state: "IL" },
  { city: "Naperville", state: "IL" },
  { city: "Indianapolis", state: "IN" },
  { city: "Fort Wayne", state: "IN" },
  { city: "Milwaukee", state: "WI" },
  { city: "Denver", state: "CO" },
  { city: "Austin", state: "TX" },
  { city: "Dallas", state: "TX" },
  { city: "Miami", state: "FL" },
];

function autoGenerate(markets, categories) {
  if (markets?.length) return markets;
  if (!categories?.length) return [];
  const out = [];
  // Fill each category with 3 cities to give the map real texture
  categories.slice(0, 6).forEach((cat, ci) => {
    DEFAULT_CITIES.slice(0, 3).forEach((loc, i) => {
      const status = ci === 0 && i === 0 ? "claimed" : i === 1 ? "available" : ci % 2 ? "pending" : "sponsored";
      out.push({
        city: loc.city,
        state: loc.state,
        category: cat.name,
        status,
        business_name: status === "claimed" || status === "sponsored" ? "Featured LocalBest" : null,
        business_slug: null,
      });
    });
  });
  return out;
}

function MarketTile({ market, index, isCenter = false, testid }) {
  const meta = STATUS_META[market.status] || STATUS_META.available;
  const Icon = meta.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03, duration: 0.35 }}
      whileHover={{ y: -3, transition: { duration: 0.2 } }}
      className={`relative rounded-xl p-3 border backdrop-blur-md ${
        isCenter ? "shadow-2xl" : ""
      }`}
      style={{
        background: `linear-gradient(135deg, ${meta.color}22 0%, rgba(15,23,42,0.85) 100%)`,
        borderColor: `${meta.color}55`,
        boxShadow: isCenter ? `0 20px 40px -12px ${meta.color}55` : "none",
      }}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-white text-sm font-bold truncate">
            {market.city}
            {market.state ? `, ${market.state}` : ""}
          </p>
          <p className="text-[11px] text-[#94A3B8] truncate">{market.category}</p>
        </div>
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: `${meta.color}33`, border: `1px solid ${meta.color}66` }}
        >
          <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
          style={{ background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}44` }}
        >
          {meta.label}
        </span>
        {market.business_slug && (
          <Link
            to={`/business/${market.business_slug}`}
            className="text-[10px] text-[#00E5FF] hover:text-white truncate max-w-[100px]"
          >
            {market.business_name || "View"}
          </Link>
        )}
      </div>
    </motion.div>
  );
}

export default function MarketMap3D({
  markets = [],
  categories = [],
  title = "Best of Best Territory Map",
  subtitle = "See which markets and categories are claimed, sponsored, or still up for grabs.",
  showFilters = true,
  compact = false,
}) {
  const enabled = flags.ENABLE_3D_MARKET_MAP;
  const { prefersReducedMotion } = useMotionPreferences();
  const [statusFilter, setStatusFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const data = useMemo(() => autoGenerate(markets, categories), [markets, categories]);

  const uniqueStates = useMemo(() => Array.from(new Set(data.map((m) => m.state).filter(Boolean))).sort(), [data]);
  const uniqueCategories = useMemo(
    () => Array.from(new Set(data.map((m) => m.category).filter(Boolean))).sort(),
    [data],
  );

  const filtered = useMemo(
    () =>
      data.filter(
        (m) =>
          (statusFilter === "all" || m.status === statusFilter) &&
          (stateFilter === "all" || m.state === stateFilter) &&
          (categoryFilter === "all" || m.category === categoryFilter),
      ),
    [data, statusFilter, stateFilter, categoryFilter],
  );

  const counts = useMemo(() => {
    const c = { claimed: 0, sponsored: 0, pending: 0, available: 0 };
    data.forEach((m) => {
      if (c[m.status] !== undefined) c[m.status] += 1;
    });
    return c;
  }, [data]);

  if (!enabled) return null;
  if (data.length === 0) return null;

  return (
    <section className="relative" data-testid="market-map-3d">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2 font-['Outfit']">
            <MapPin className="w-5 h-5 text-[#F59E0B]" />
            {title}
          </h2>
          {subtitle && <p className="text-sm text-[#94A3B8] mt-1">{subtitle}</p>}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <span
              key={key}
              className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full whitespace-nowrap"
              style={{
                background: `${meta.color}18`,
                color: meta.color,
                border: `1px solid ${meta.color}44`,
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: meta.color }}
              />
              {counts[key]} {meta.label.split(" ").slice(0, 2).join(" ")}
            </span>
          ))}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="market-map-3d-filters">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs [&>option]:bg-[#0F172A]"
            aria-label="Filter by status"
            data-testid="market-map-3d-filter-status"
          >
            <option value="all">All statuses</option>
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <option key={key} value={key}>{meta.label}</option>
            ))}
          </select>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs [&>option]:bg-[#0F172A]"
            aria-label="Filter by state"
            data-testid="market-map-3d-filter-state"
          >
            <option value="all">All states</option>
            {uniqueStates.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs [&>option]:bg-[#0F172A]"
            aria-label="Filter by category"
            data-testid="market-map-3d-filter-category"
          >
            <option value="all">All categories</option>
            {uniqueCategories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="text-xs text-[#94A3B8] ml-auto">
            {filtered.length} of {data.length} markets shown
          </span>
        </div>
      )}

      {/* Ambient depth glow behind the map (skipped for reduced-motion) */}
      {!prefersReducedMotion && !compact && (
        <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden -z-10">
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[60%] rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(245,158,11,0.10), transparent 65%)",
              filter: "blur(60px)",
            }}
          />
        </div>
      )}

      {/* Tile grid */}
      <div
        className={`grid gap-3 ${
          compact ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        }`}
        data-testid="market-map-3d-grid"
      >
        {filtered.slice(0, compact ? 8 : 24).map((m, i) => (
          <MarketTile
            key={`${m.city}-${m.state}-${m.category}-${i}`}
            market={m}
            index={i}
            testid={`market-map-3d-tile-${i}`}
          />
        ))}
      </div>

      {filtered.length > (compact ? 8 : 24) && (
        <div className="mt-5 text-center">
          <Link
            to="/markets"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm border border-white/10"
            data-testid="market-map-3d-view-all"
          >
            View all {filtered.length} territories
          </Link>
        </div>
      )}
    </section>
  );
}
